import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtQty, fmtPct } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { RefreshCw, AlertTriangle, ArrowUpRight, ChevronRight, Wallet, Building2, Zap } from "lucide-react";

const COLORS = ["#34d399", "#a3e635", "#fbbf24", "#f472b6", "#a78bfa", "#60a5fa", "#f87171", "#4ade80"];

// The three account slots the user can flip between. "Aanar Paper" is our
// internal ledger (always available); the two IBKR slots are the same MCP
// connection viewed through the lens of the user-declared account_type.
// IBKR MCP only supports one OAuth connection per user, so the "wrong" IBKR
// slot renders a helpful CTA instead of pretending to have data.
const SLOTS = [
  { id: "aanar_paper", label: "Aanar Paper", icon: Wallet, hint: "Aanar's virtual ledger" },
  { id: "ibkr_paper", label: "IBKR Paper", icon: Building2, hint: "Your IBKR paper account" },
  { id: "ibkr_live", label: "IBKR Live", icon: Zap, hint: "Your IBKR live account" },
];
const SLOT_STORAGE = "aanar_broker_slot";

/**
 * Broker Analytics — real IBKR account view via native MCP.
 * Powered by IBKR MCP tools:
 *   - get_account_summary                 → equity, cash, buying power
 *   - get_account_positions               → open positions
 *   - get_pa_performance_all_periods      → equity curve
 *   - get_pa_allocation                   → asset-class / sector allocation
 *   - get_account_trades                  → recent fills blotter
 *   - get_account_orders                  → working orders
 *   - get_order_instructions              → pending AI Instructions drafts
 */
export default function IBKRBoard() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [trades, setTrades] = useState(null);
  const [orders, setOrders] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("1Y");
  // Selected account slot — persists across visits so users don't lose context.
  const [slot, setSlot] = useState(() => {
    try { return localStorage.getItem(SLOT_STORAGE) || "aanar_paper"; }
    catch { return "aanar_paper"; }
  });
  // Aanar's internal paper ledger (only fetched when slot === "aanar_paper").
  const [aanarPaper, setAanarPaper] = useState(null);

  useEffect(() => { try { localStorage.setItem(SLOT_STORAGE, slot); } catch {} }, [slot]);

  // Whether the current slot's data is actually available (avoids surprise
  // when IBKR is connected as paper but user clicked "IBKR Live").
  const slotAvailable = (() => {
    if (slot === "aanar_paper") return true;
    if (!status?.connected) return false;
    const wantType = slot === "ibkr_live" ? "live" : "paper";
    // If user hasn't declared account_type yet, allow either IBKR slot so
    // they can see data — the Settings link on the "wrong slot" empty state
    // still nudges them to declare it.
    const at = status.account_type;
    return !at || at === wantType;
  })();

  const load = async () => {
    setLoading(true);
    try {
      // Always refresh IBKR status — cheap and needed by the switcher UI.
      const s = await api.get("/ibkr/mcp/status");
      setStatus(s.data);
      if (slot === "aanar_paper") {
        // Aanar Paper: single call to our internal ledger endpoint.
        try {
          const p = await api.get("/portfolio");
          setAanarPaper(p.data);
        } catch {
          setAanarPaper({ error: "Couldn't load Aanar portfolio" });
        }
        setRefreshedAt(new Date());
      } else if (s.data?.connected) {
        const wantType = slot === "ibkr_live" ? "live" : "paper";
        // If IBKR is connected but declared as the OTHER type, don't hammer
        // the MCP with pulls that would just confuse the user's expectations.
        if (s.data.account_type && s.data.account_type !== wantType) {
          setRefreshedAt(new Date());
        } else {
          const results = await Promise.allSettled([
            api.get("/ibkr/mcp/summary"),
            api.get("/ibkr/mcp/positions"),
            api.get("/ibkr/mcp/drafts"),
            api.get("/ibkr/mcp/performance", { params: { period } }),
            api.get("/ibkr/mcp/allocation"),
            api.get("/ibkr/mcp/trades", { params: { days: 7 } }),
            api.get("/ibkr/mcp/orders"),
          ]);
          const [sum, pos, dr, perf, alloc, tr, ord] = results;
          if (sum.status === "fulfilled") setSummary(sum.value.data);
          if (pos.status === "fulfilled") setPositions(pos.value.data);
          if (dr.status === "fulfilled") setDrafts(dr.value.data?.drafts || []);
          if (perf.status === "fulfilled") setPerformance(perf.value.data); else setPerformance({ error: perf.reason?.response?.data?.detail || "unavailable" });
          if (alloc.status === "fulfilled") setAllocation(alloc.value.data); else setAllocation({ error: alloc.reason?.response?.data?.detail || "unavailable" });
          if (tr.status === "fulfilled") setTrades(tr.value.data); else setTrades({ error: tr.reason?.response?.data?.detail || "unavailable" });
          if (ord.status === "fulfilled") setOrders(ord.value.data); else setOrders({ error: ord.reason?.response?.data?.detail || "unavailable" });
          setRefreshedAt(new Date());
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [period, slot]);

  const s = summary?.summary || {};
  // ---- Slot-aware derived values ----------------------------------------
  // For "Aanar Paper" we shape the internal /portfolio response into the same
  // top-tile / positions / allocation-pie contract the IBKR views use so the
  // renderers below don't have to branch on slot for every scalar. Anything
  // IBKR-only (drafts, working orders, PA equity curve, trade blotter) simply
  // renders empty when the slot is aanar_paper — we hide those sections.
  const isAanar = slot === "aanar_paper";
  const isIBKR = !isAanar;
  const posRaw = isAanar ? (aanarPaper?.holdings || [])
                          : (positions?.positions?.positions || positions?.positions || []);
  const posArr = Array.isArray(posRaw) ? posRaw : [];
  const netLiq = isAanar
    ? Number(aanarPaper?.total_equity || 0)
    : Number(s.net_liquidation || s.equity_with_loan_value || 0);
  const cash = isAanar
    ? Number(aanarPaper?.cash_balance || 0)
    : Number(s.total_cash_value || s.cash || 0);
  const buyingPower = isAanar ? cash : Number(s.buying_power || 0);
  const unrealizedPnl = isAanar
    ? Number(aanarPaper?.total_pnl || 0)
    : posArr.reduce((sum, p) => sum + Number(p.unrealized_pnl || 0), 0);

  // Position-derived allocation (fallback if PA allocation call didn't return usable data).
  // Aanar holdings use {symbol,value}; IBKR positions use {symbol,market_value}.
  const posAllocData = useMemo(() => {
    const rows = posArr
      .map((p) => ({
        name: p.symbol || p.ticker || "?",
        value: Number(p.value ?? p.market_value ?? 0),
      }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    if (cash > 0) rows.push({ name: "Cash", value: cash });
    return rows;
  }, [posArr, cash]);

  // Prefer PA allocation grouping (asset_class buckets) when the MCP tool returned it — IBKR slots only.
  const paAllocData = useMemo(() => {
    if (isAanar) return null;
    const raw = allocation?.allocation;
    if (!raw || raw.error) return null;
    const groups = raw.asset_class || raw.assetClass || raw.by_asset_class || raw.groups;
    if (!groups) return null;
    const arr = Array.isArray(groups)
      ? groups.map((g) => ({ name: g.name || g.category || g.key || "?", value: Number(g.value || g.marketValue || g.amount || 0) }))
      : Object.entries(groups).map(([k, v]) => ({ name: k, value: Number(typeof v === "object" ? (v.value || v.marketValue || 0) : v) }));
    return arr.filter((x) => x.value > 0).slice(0, 10);
  }, [allocation, isAanar]);

  const allocData = (paAllocData && paAllocData.length > 0) ? paAllocData : posAllocData;

  // Performance equity curve
  const perfSeries = useMemo(() => {
    const p = performance?.performance;
    if (!p || p.error) return null;
    // Try common IBKR PA shapes
    const nav = p.nav || p.NAV || p.equity || p.nav_series || p.timeseries;
    if (Array.isArray(nav)) {
      return nav.map((row) => {
        if (typeof row === "number") return { t: "", v: row };
        return {
          t: row.date || row.time || row.t || "",
          v: Number(row.value ?? row.nav ?? row.equity ?? row.v ?? 0),
        };
      }).filter((r) => r.v > 0);
    }
    // Or {dates: [...], values: [...]}
    if (p.dates && p.values && p.dates.length === p.values.length) {
      return p.dates.map((t, i) => ({ t, v: Number(p.values[i] || 0) }));
    }
    return null;
  }, [performance]);

  const periodReturn = useMemo(() => {
    if (!perfSeries || perfSeries.length < 2) return null;
    const first = perfSeries[0].v, last = perfSeries[perfSeries.length - 1].v;
    if (!first) return null;
    return ((last - first) / first) * 100;
  }, [perfSeries]);

  // Trades blotter
  const tradesArr = useMemo(() => {
    const t = trades?.trades;
    if (!t || t.error) return [];
    const arr = Array.isArray(t) ? t : (t.trades || t.fills || t.executions || []);
    return Array.isArray(arr) ? arr : [];
  }, [trades]);

  // Working orders
  const ordersArr = useMemo(() => {
    const o = orders?.orders;
    if (!o || o.error) return [];
    const arr = Array.isArray(o) ? o : (o.orders || o.working || o.open_orders || []);
    return Array.isArray(arr) ? arr : [];
  }, [orders]);

  if (!status) return <div className="text-muted-foreground">Loading…</div>;

  // The segmented control is ALWAYS rendered — even when IBKR isn't connected —
  // so users can flip to "Aanar Paper" without needing to connect first.
  const SlotSwitcher = (
    <div className="flex gap-1 p-1 rounded-full bg-secondary/30 border border-border/40 w-fit"
         data-testid="broker-slot-switcher">
      {SLOTS.map((sl) => {
        const Icon = sl.icon;
        const active = slot === sl.id;
        return (
          <button key={sl.id} onClick={() => setSlot(sl.id)}
                  data-testid={`broker-slot-${sl.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    active
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                      : "text-muted-foreground hover:text-foreground"
                  }`}>
            <Icon className="w-3.5 h-3.5" /> {sl.label}
          </button>
        );
      })}
    </div>
  );

  // If IBKR slot is selected but not connected → empty-state with switcher.
  if (isIBKR && !status.connected) {
    return (
      <div className="space-y-6" data-testid="broker-analytics-page">
        {SlotSwitcher}
        <Card className="p-8 rounded-2xl border-border text-center" data-testid="broker-connect-card">
          <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4 text-lg font-bold">IBKR</div>
          <h2 className="font-display text-2xl font-bold mb-2">Connect your IBKR {slot === "ibkr_live" ? "Live" : "Paper"} account</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            You're viewing the {slot === "ibkr_live" ? "Live" : "Paper"} slot. Connect your IBKR account in Settings to see performance, allocation, orders, and fills.
          </p>
          <Link to="/settings" data-testid="broker-connect-link">
            <Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
              Connect IBKR <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // If IBKR is connected but user picked the OTHER type slot, tell them.
  if (isIBKR && !slotAvailable) {
    const wantType = slot === "ibkr_live" ? "Live" : "Paper";
    const hasType = status.account_type === "live" ? "Live" : "Paper";
    return (
      <div className="space-y-6" data-testid="broker-analytics-page">
        {SlotSwitcher}
        <Card className="p-8 rounded-2xl border-amber-500/40 bg-amber-500/[0.04] text-center" data-testid="broker-wrong-slot-card">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold mb-2">Your connected IBKR account is labelled {hasType}</h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            Aanar can only stream data for the account you're currently connected to. Either switch to the "IBKR {hasType}" tab above, or disconnect and reconnect a {wantType} account in Settings.
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button variant="outline" onClick={() => setSlot(hasType === "Live" ? "ibkr_live" : "ibkr_paper")}
                    className="rounded-full" data-testid="broker-switch-to-connected">
              Switch to IBKR {hasType}
            </Button>
            <Link to="/settings" data-testid="broker-reconnect-link">
              <Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
                Reconnect <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="broker-analytics-page">
      {SlotSwitcher}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Broker Analytics</div>
            {isAanar ? (
              <Badge className="border-0 text-[10px] font-mono-tab uppercase tracking-wider bg-blue-500/15 text-blue-400"
                     data-testid="broker-slot-badge">
                Aanar · Paper
              </Badge>
            ) : (
              <Badge className={`border-0 text-[10px] font-mono-tab uppercase tracking-wider ${
                slot === "ibkr_live"
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "bg-amber-500/15 text-amber-400"
              }`} data-testid="broker-slot-badge">
                IBKR · {slot === "ibkr_live" ? "Live" : "Paper"}
              </Badge>
            )}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="broker-slot-title">
            {isAanar ? "Your Aanar paper account" : `Your IBKR ${slot === "ibkr_live" ? "live" : "paper"} account`}
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            Refreshed {refreshedAt ? refreshedAt.toLocaleTimeString() : "…"} · streams every 30s
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="broker-refresh"
                className="rounded-full">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Top tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="broker-tiles">
        <Tile label="Net Liquidation" value={fmtMoney(netLiq)} />
        <Tile label="Cash" value={fmtMoney(cash)} />
        <Tile label="Buying Power" value={fmtMoney(buyingPower)} />
        <Tile label="Unrealized P&L" value={fmtMoney(unrealizedPnl)} accent={unrealizedPnl >= 0 ? "gain" : "loss"} />
      </div>

      {/* Pending drafts — IBKR only */}
      {isIBKR && drafts.length > 0 && (
        <Card className="p-4 rounded-2xl border-amber-500/40 bg-amber-500/[0.04]" data-testid="broker-drafts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <div className="font-display font-bold text-sm">{drafts.length} draft{drafts.length > 1 ? "s" : ""} awaiting your confirmation in IBKR</div>
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            Open IBKR mobile/desktop → <span className="font-semibold text-foreground">Orders &amp; Trades → AI Instructions</span> to confirm each draft.
          </div>
          <div className="space-y-1 text-[11px] font-mono-tab">
            {drafts.slice(0, 6).map((d, i) => (
              <div key={i} className="flex justify-between px-2 py-1 rounded bg-black/20">
                <span>{d.side?.toUpperCase()} {fmtQty(d.quantity)} {d.symbol}</span>
                <span>{d.limit_price ? `@ ${fmtMoney(d.limit_price)}` : "MKT"}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Performance chart — IBKR PA only */}
      {isIBKR && (
      <Card className="p-4 rounded-2xl border-border" data-testid="broker-performance">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Performance</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="font-display text-2xl font-bold font-mono-tab">{fmtMoney(netLiq)}</div>
              {periodReturn !== null && (
                <Badge className={`border-0 text-[11px] font-mono-tab ${periodReturn >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                  {periodReturn >= 0 ? "▲" : "▼"} {fmtPct(Math.abs(periodReturn / 100))}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-1" data-testid="perf-period-tabs">
            {["1D", "1W", "1M", "3M", "YTD", "1Y"].map((p) => (
              <button key={p} onClick={() => setPeriod(p)}
                      data-testid={`perf-period-${p}`}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-mono-tab transition-colors ${
                        period === p
                          ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                          : "bg-secondary/30 text-muted-foreground hover:text-foreground border border-transparent"
                      }`}>
                {p}
              </button>
            ))}
          </div>
        </div>
        {perfSeries && perfSeries.length > 1 ? (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={perfSeries} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="perfFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))"
                       domain={["auto", "auto"]}
                       tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                         formatter={(v) => [fmtMoney(v), "NAV"]} />
                <Area type="monotone" dataKey="v" stroke="#34d399" strokeWidth={2} fill="url(#perfFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center text-xs text-muted-foreground italic text-center px-4">
            {performance?.performance?.error || performance?.error
              ? "IBKR Portfolio Analytics didn't return an equity curve for this account. This usually means PA reporting hasn't been enabled — go to Account Management → Reports → Portfolio Analyst to enable it."
              : "Loading equity curve…"}
          </div>
        )}
      </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Allocation pie */}
        <Card className="p-4 rounded-2xl border-border" data-testid="broker-allocation">
          <div className="text-xs uppercase text-muted-foreground mb-2">
            Allocation {paAllocData ? "· by asset class" : "· by position"}
          </div>
          {allocData.length === 0 ? (
            <div className="text-sm text-muted-foreground italic h-64 flex items-center justify-center">
              No positions — 100% cash
            </div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={allocData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {allocData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                           formatter={(v, n) => [fmtMoney(v), n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Positions blotter */}
        <Card className="p-4 rounded-2xl border-border" data-testid="broker-positions">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase text-muted-foreground">Open positions ({posArr.length})</div>
          </div>
          {posArr.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-6 text-center">
              {isAanar
                ? "You haven't taken any positions in Aanar Paper yet. Head to Markets to place your first paper trade."
                : "You don't hold any positions in IBKR right now."}
            </div>
          ) : (
            <div className="rounded-lg border border-border/40 divide-y divide-border/40 overflow-hidden">
              {posArr.slice(0, 10).map((p, i) => {
                const pnl = Number(p.unrealized_pnl ?? p.pnl ?? 0);
                const mv = Number(p.market_value ?? p.value ?? 0);
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`position-row-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold">{p.symbol || p.ticker}</span>
                      <span className="text-[10px] text-muted-foreground uppercase">
                        {p.assetClass || p.asset_class || p.asset_type || "stk"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 font-mono-tab">
                      <span>{fmtQty(Number(p.quantity ?? p.position ?? 0))}</span>
                      <span>{fmtMoney(mv)}</span>
                      <span className={pnl >= 0 ? "gain" : "loss"}>{fmtMoney(pnl)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Working orders + Trade blotter — IBKR only (Aanar Paper orders live on /orders) */}
      {isIBKR && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Working orders */}
        <Card className="p-4 rounded-2xl border-border" data-testid="broker-working-orders">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase text-muted-foreground">Working orders ({ordersArr.length})</div>
          </div>
          {ordersArr.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-6 text-center">
              {orders?.orders?.error || orders?.error
                ? "IBKR didn't return live order data for this account."
                : "No live orders sitting in IBKR."}
            </div>
          ) : (
            <div className="rounded-lg border border-border/40 divide-y divide-border/40 overflow-hidden">
              {ordersArr.slice(0, 12).map((o, i) => {
                const side = (o.side || o.action || "").toUpperCase();
                const px = o.limit_price || o.price || o.stop_price || o.aux_price;
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`working-order-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge className={`border-0 text-[9px] font-mono-tab ${
                        side === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                      }`}>
                        {side || "?"}
                      </Badge>
                      <span className="font-semibold">{o.symbol || o.ticker}</span>
                      <span className="text-[10px] text-muted-foreground uppercase">{o.order_type || o.type || "LMT"}</span>
                    </div>
                    <div className="flex items-center gap-3 font-mono-tab text-muted-foreground">
                      <span className="text-foreground">{fmtQty(Number(o.quantity ?? o.qty ?? 0))}</span>
                      <span>{px ? `@ ${fmtMoney(Number(px))}` : "MKT"}</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Trade blotter */}
        <Card className="p-4 rounded-2xl border-border" data-testid="broker-trades">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase text-muted-foreground">Recent fills · 7 days ({tradesArr.length})</div>
          </div>
          {tradesArr.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-6 text-center">
              {trades?.trades?.error || trades?.error
                ? "IBKR didn't return recent trade data."
                : "No fills in the last 7 days."}
            </div>
          ) : (
            <div className="rounded-lg border border-border/40 divide-y divide-border/40 overflow-hidden max-h-72 overflow-y-auto">
              {tradesArr.slice(0, 20).map((t, i) => {
                const side = (t.side || t.action || "").toUpperCase();
                const px = t.price || t.avg_price || t.fill_price;
                const when = t.time || t.execution_time || t.timestamp || t.date;
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`fill-row-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge className={`border-0 text-[9px] font-mono-tab ${
                        side === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                      }`}>
                        {side || "?"}
                      </Badge>
                      <span className="font-semibold">{t.symbol || t.ticker}</span>
                    </div>
                    <div className="flex items-center gap-3 font-mono-tab text-muted-foreground">
                      <span className="text-foreground">{fmtQty(Number(t.quantity ?? t.qty ?? 0))}</span>
                      <span>@ {fmtMoney(Number(px || 0))}</span>
                      {when && <span className="text-[10px]">{String(when).slice(5, 16)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
      )}

      <div className="text-[10px] text-muted-foreground text-center">
        {isAanar
          ? "Aanar Paper trades on our virtual ledger — practice risk-free before wiring a live broker"
          : "Data streams from IBKR via native MCP · Aanar never places live orders directly — every trade appears as a draft in your IBKR AI Instructions tab"}
      </div>
    </div>
  );
}

function Tile({ label, value, accent }) {
  const cls = accent === "gain" ? "gain" : accent === "loss" ? "loss" : "";
  return (
    <div className="rounded-xl bg-secondary/30 border border-border/50 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono-tab text-lg font-semibold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
